/**
 * Axios Refresh-Token Interceptor (Iter 329g / Phase 2).
 *
 * Global gilt:
 *   - Wenn ein API-Call mit gültigem Authorization-Header ein 401 bekommt,
 *     versucht der Interceptor EINMAL den Access-Token mit dem aktuell
 *     gespeicherten Refresh-Token zu erneuern und wiederholt dann den
 *     Original-Call.
 *   - Single-Flight: wenn 5 parallele Calls gleichzeitig 401 werfen,
 *     starten wir NUR EINE Refresh-Anfrage — alle anderen warten auf
 *     dieselbe Promise. Verhindert Token-Rotation-Selbstkill.
 *   - Wenn der Refresh fehlschlägt → onRefreshFail() (= globaler Logout).
 *
 * Setup wird einmalig aus AuthContext aufgerufen.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let refreshInFlight = null;
let isInstalled = false;

export function setupAxiosRefreshInterceptor({
  getRefreshToken,
  getAccessToken,
  onRefreshSuccess,
  onRefreshFail,
}) {
  if (isInstalled) {
    return;
  }
  isInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      // Nur 401 + nur Calls die einen Auth-Header trugen behandeln.
      // Refresh-Endpoint selbst MUSS ausgenommen sein (sonst Endlos-Loop).
      const urlPath = String(original?.url || '');
      const isAuthEndpoint =
        urlPath.endsWith('/api/auth/refresh') ||
        urlPath.endsWith('/api/auth/login') ||
        urlPath.endsWith('/api/auth/logout');

      if (status !== 401 || isAuthEndpoint || original?._retry) {
        return Promise.reject(error);
      }

      const hadAuth =
        original?.headers?.Authorization ||
        original?.headers?.authorization ||
        axios.defaults.headers.common['Authorization'];
      if (!hadAuth) {
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken?.();
      if (!refreshToken) {
        // Kein RT vorhanden → wir können nichts tun, klassischer Logout.
        try { onRefreshFail?.(); } catch (e) { /* noop */ }
        return Promise.reject(error);
      }

      // Single-Flight: gleiche Promise für alle parallelen Refreshs.
      if (!refreshInFlight) {
        refreshInFlight = (async () => {
          try {
            const { data } = await axios.post(
              `${API}/auth/refresh`,
              { refresh_token: refreshToken },
              { _retry: true, headers: { Authorization: undefined } } // kein AT zum Refresh schicken
            );
            if (!data?.access_token) {
              throw new Error('refresh-no-token');
            }
            onRefreshSuccess?.(data);
            return data.access_token;
          } catch (refreshErr) {
            try { onRefreshFail?.(); } catch (e) { /* noop */ }
            throw refreshErr;
          } finally {
            // In-Flight im NEXT tick zurücksetzen, damit alle wartenden
            // Original-Calls ihn noch greifen können
            setTimeout(() => { refreshInFlight = null; }, 0);
          }
        })();
      }

      try {
        const newAccessToken = await refreshInFlight;
        // Original-Request mit neuem Token wiederholen
        original._retry = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccessToken}`;
        return axios(original);
      } catch (e) {
        return Promise.reject(error);
      }
    },
  );

  return () => {
    // (Test-Hook: nicht produktiv genutzt) — Interceptor wieder freigeben
    isInstalled = false;
  };
}

/**
 * Test-Helper: setzt den internen In-Flight-Status zurück.
 * NUR FÜR TESTS — nicht im Produktiv-Pfad aufrufen.
 */
export function _resetForTests() {
  refreshInFlight = null;
  isInstalled = false;
}
